import { ActionIcon, Button, Menu, Pagination, Select, Skeleton, Tabs, Text } from '@mantine/core';
import {
  IconArrowBackUp,
  IconArrowsExchange,
  IconDots,
  IconEdit,
  IconFileSpreadsheet,
  IconFilterOff,
  IconHistory,
  IconLogout,
  IconPlus,
  IconTransfer,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { CreateLoginDialog, LoginOffer, loginOfferAfterSignOn } from './CrewCardDialog.js';
import classes from './CrewListScreen.module.css';
import { usePersonnelExport } from './crew-exports.js';
import {
  AmendDialog,
  HistoryDialog,
  type Option,
  RankChangeDialog,
  ReasonDialog,
  SignOffDialog,
  SignOnDialog,
} from './MovementDialogs.js';
import {
  defaultMovementRange,
  MOVEMENT_PAGE_SIZE,
  type MovementAction,
  type MovementRights,
  movementActionHint,
  movementPageOf,
  movementRanks,
  rowActions,
  signOnPeople,
  viewMovements,
} from './sign-on-off.js';
import { errorCode, type PersonnelSource } from './source.js';
import type {
  CrewMovementList,
  CrewMovementRow,
  CrewProfileView,
  MovementResult,
  MovementTab,
  PersonnelVessel,
} from './types.js';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: CrewMovementList; vessels: PersonnelVessel[]; people: Option[] };

type Open = { action: MovementAction | 'signOn'; row: CrewMovementRow | null };

const SKELETON_ROWS = 6;

/**
 * Crew ▸ Sign On / Off (`gemi-personel.jpeg` panel 5; ADR 0131 §3, §7): Joining ve Sign-Off
 * sekmeleri, gemi / rütbe / tarih aralığı süzgeci, Pending / Completed rozeti. Ofiste "+ Add
 * Joining" ve satır menüsü (Amend, Rank change, Sign off, Cancel joining, Revoke sign-off,
 * History); gemide liste salt okunurdur.
 */
export function SignOnOffScreen({ source }: { source: PersonnelSource }) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) => t(`signOnOff.${key}`, options);
  const shell = useShell();
  const locale = i18n.resolvedLanguage ?? 'en';
  const web = source.scope === 'web';
  const editor = web ? source.movementEditor : undefined;
  const cards = web ? source.crewCards : undefined;
  const [tab, setTab] = useState<MovementTab>('joining');
  // Varsayılan aralık bu yıl (tasarım 5); yıl dönümünde gemi ile istemci günü ayrışsa da aralık seçilebilir
  const [range, setRange] = useState(() =>
    defaultMovementRange(new Date().toISOString().slice(0, 10)),
  );
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [open, setOpen] = useState<Open | null>(null);
  const exporter = usePersonnelExport(source);
  const [exportError, setExportError] = useState<string | null>(null);
  /** Sign On sonrası hesap önerisi (ortak 2026-10-30 madde 9): hesapsız kişinin kartı. */
  const [offer, setOffer] = useState<CrewProfileView | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);

  const load = useCallback(() => {
    if (!source.movements) return;
    const movements = source.movements;
    setState({ kind: 'loading' });
    (async () => {
      const [vessels, list] = await Promise.all([source.vessels(), movements({ tab, ...range })]);
      // Yönetici olmayan kendini katılışa yazamaz (U39; ADR 0151 §4)
      const people: Option[] =
        editor && list.canSignOn
          ? signOnPeople(
              (await source.crew()).crew
                .filter((member) => member.hasCard !== false)
                .map((member) => ({
                  value: member.userId,
                  label: member.crewCode
                    ? `${member.fullName} (${member.crewCode})`
                    : member.fullName,
                }))
                .sort((x, y) => x.label.localeCompare(y.label, locale)),
              list,
            )
          : [];
      setState({ kind: 'ready', list, vessels, people });
    })().catch((error: unknown) => setState({ kind: 'error', code: errorCode(error) }));
  }, [source, tab, range, editor, locale]);
  useEffect(load, [load]);

  const list = state.kind === 'ready' ? state.list : null;
  const rows = list?.rows ?? [];
  const vessels = state.kind === 'ready' ? state.vessels : [];
  const ranks = useMemo(() => movementRanks(rows, locale), [rows, locale]);
  const view = useMemo(() => viewMovements(rows, { vesselId, roleId }), [rows, vesselId, roleId]);
  const pages = Math.max(1, Math.ceil(view.length / MOVEMENT_PAGE_SIZE));
  const current = Math.min(Math.max(page, 1), pages);
  const visible = movementPageOf(view, current);
  const filtered = vesselId !== null || roleId !== null;
  // Satır işlemleri: kendi satırı ve Rank change için `canCreate` (ADR 0151 §4)
  const can: MovementRights | null = editor && list ? list : null;
  const shipName = web ? null : (vessels[0]?.name ?? null);
  const today = list?.today ?? new Date().toISOString().slice(0, 10);
  const close = () => setOpen(null);
  const saved = () => {
    setOpen(null);
    load();
  };
  /** Katılış yazıldı: kişinin hesabı yoksa ve yetki varsa (kararı sunucu verir) öneri çıkar. */
  const signedOn = (_result: MovementResult, userId: string) => {
    saved();
    if (web) void loginOfferAfterSignOn(source, userId).then(setOffer);
  };
  const profilePath = (userId: string) => `/crew/profile/${encodeURIComponent(userId)}`;

  const header = (
    <PageHeader
      title={k('title')}
      description={shipName ? k('subtitleShip', { vessel: shipName }) : k('subtitle')}
    />
  );

  if (!source.movements) return header;

  if (state.kind === 'error') {
    return (
      <div className={classes.page}>
        {header}
        <Panel>
          <EmptyState
            tone="danger"
            title={k('error')}
            description={t('common.loadError')}
            action={
              <Button size="xs" variant="light" onClick={load}>
                {t('common.retry')}
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      {header}
      <Tabs
        value={tab}
        onChange={(value) => {
          setTab((value ?? 'joining') as MovementTab);
          setPage(1);
        }}
      >
        <Tabs.List aria-label={k('tabs.label')}>
          <Tabs.Tab value="joining">{k('tabs.joining')}</Tabs.Tab>
          <Tabs.Tab value="sign_off">{k('tabs.sign_off')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {offer ? (
        <LoginOffer
          name={offer.fullName}
          onCreate={() => setLoginOpen(true)}
          onSkip={() => setOffer(null)}
        />
      ) : null}

      <Panel flush aria-label={k('table')}>
        <section className={classes.filters} aria-label={k('filters')}>
          {web ? (
            <Select
              className={classes.select}
              aria-label={k('vessel')}
              data={[
                { value: '', label: k('allVessels') },
                ...vessels.map((vessel) => ({ value: vessel.id, label: vessel.name })),
              ]}
              value={vesselId ?? ''}
              allowDeselect={false}
              onChange={(value) => {
                setVesselId(value || null);
                setPage(1);
              }}
            />
          ) : null}
          <Select
            className={classes.select}
            aria-label={k('rank')}
            data={[{ value: '', label: k('allRanks') }, ...ranks]}
            value={roleId ?? ''}
            allowDeselect={false}
            onChange={(value) => {
              setRoleId(value || null);
              setPage(1);
            }}
          />
          <DayInput
            className={classes.select}
            aria-label={k('from')}
            value={range.from}
            maxDate={range.to}
            onChange={(value) => {
              if (value) setRange({ ...range, from: value });
              setPage(1);
            }}
          />
          <DayInput
            className={classes.select}
            aria-label={k('to')}
            value={range.to}
            minDate={range.from}
            onChange={(value) => {
              if (value) setRange({ ...range, to: value });
              setPage(1);
            }}
          />
          <Button
            className={classes.add}
            variant="default"
            leftSection={<IconFileSpreadsheet size={16} aria-hidden />}
            loading={exporter.busy}
            disabled={state.kind !== 'ready' || view.length === 0}
            onClick={() => {
              setExportError(null);
              // Süzgeçten geçen bütün satırlar (sayfadan bağımsız; ADR 0131 Ek 3)
              void exporter.movements(view, tab, range).then(setExportError);
            }}
          >
            {k('exportExcel')}
          </Button>
          {editor && list?.canSignOn && tab === 'joining' ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setOffer(null);
                setOpen({ action: 'signOn', row: null });
              }}
            >
              {k('addJoining')}
            </Button>
          ) : null}
        </section>
        {exportError ? (
          <p role="alert" className={classes.exportError}>
            {exportError}
          </p>
        ) : null}

        {state.kind === 'ready' && rows.length === 0 ? (
          <EmptyState
            icon={<IconTransfer size={22} stroke={1.8} />}
            title={web ? k('empty') : k('emptyShip')}
            description={web ? undefined : k('readOnlyShip')}
          />
        ) : state.kind === 'ready' && view.length === 0 ? (
          <EmptyState
            icon={<IconFilterOff size={22} stroke={1.8} />}
            title={k('noMatch')}
            action={
              filtered ? (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    setVesselId(null);
                    setRoleId(null);
                    setPage(1);
                  }}
                >
                  {t('common.clearFilters')}
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <section
              className={classes.tableWrap}
              aria-label={k('table')}
              aria-busy={state.kind === 'loading' || undefined}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <MovementTable
                rows={visible}
                tab={tab}
                web={web}
                loading={state.kind === 'loading'}
                can={can}
                onProfile={shell ? (userId) => shell.navigate(profilePath(userId)) : undefined}
                onAction={(action, row) => setOpen({ action, row })}
              />
            </section>
            {state.kind === 'ready' ? (
              <div className={classes.pager}>
                <span>
                  {k('showing', {
                    from: (current - 1) * MOVEMENT_PAGE_SIZE + 1,
                    to: Math.min(current * MOVEMENT_PAGE_SIZE, view.length),
                    total: view.length,
                  })}
                </span>
                {pages > 1 ? (
                  <Pagination
                    total={pages}
                    value={current}
                    onChange={setPage}
                    size="sm"
                    aria-label={k('pagination')}
                    getControlProps={(control) => ({ 'aria-label': k(`page.${control}`) })}
                  />
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </Panel>

      {editor && open?.action === 'signOn' && state.kind === 'ready' ? (
        <SignOnDialog
          editor={editor}
          people={state.people}
          vessels={state.vessels.map((vessel) => ({ value: vessel.id, label: vessel.name }))}
          onClose={close}
          onSaved={signedOn}
        />
      ) : null}
      {cards && offer && loginOpen ? (
        <CreateLoginDialog
          view={offer}
          editor={cards}
          onClose={() => setLoginOpen(false)}
          onSaved={() => {
            setLoginOpen(false);
            setOffer(null);
          }}
        />
      ) : null}
      {editor && open?.row && open.action === 'signOff' ? (
        <SignOffDialog editor={editor} target={open.row} onClose={close} onSaved={saved} />
      ) : null}
      {editor && open?.row && open.action === 'rankChange' ? (
        <RankChangeDialog
          editor={editor}
          target={open.row}
          today={today}
          onClose={close}
          onSaved={saved}
        />
      ) : null}
      {editor && open?.row && open.action === 'amend' ? (
        <AmendDialog
          editor={editor}
          target={open.row}
          tab={tab}
          today={today}
          onClose={close}
          onSaved={saved}
        />
      ) : null}
      {editor && open?.row && (open.action === 'cancel' || open.action === 'revoke') ? (
        <ReasonDialog
          editor={editor}
          target={open.row}
          mode={open.action}
          today={today}
          onClose={close}
          onSaved={saved}
        />
      ) : null}
      {editor && open?.row && open.action === 'history' ? (
        <HistoryDialog editor={editor} target={open.row} onClose={close} />
      ) : null}
    </div>
  );
}

/**
 * Sign On / Off tablosu (tasarım 5): Name, Rank, (Vessel), Joining / Sign-Off Date, Port,
 * (Reason), Status, Actions. Pencereden ayrı: testte statik çizilir.
 */
export function MovementTable({
  rows,
  tab,
  web,
  loading = false,
  can,
  onProfile,
  onAction,
}: {
  rows: readonly CrewMovementRow[];
  tab: MovementTab;
  web: boolean;
  loading?: boolean;
  /** `null`: gemi ya da düzenleyici yok; satır menüsünde yalnız Crew Profile. */
  can: MovementRights | null;
  onProfile?: ((userId: string) => void) | undefined;
  onAction: (action: MovementAction, row: CrewMovementRow) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) => t(`signOnOff.${key}`, options);
  const icons: Record<MovementAction, typeof IconEdit> = {
    signOff: IconLogout,
    rankChange: IconArrowsExchange,
    amend: IconEdit,
    cancel: IconX,
    revoke: IconArrowBackUp,
    history: IconHistory,
  };
  const actions = (row: CrewMovementRow) => {
    const items = rowActions(row, tab, can);
    if (!onProfile && items.length === 0) return null;
    return (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            className={classes.more}
            aria-label={k('actionsFor', { name: row.fullName })}
          >
            <IconDots size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {onProfile ? (
            <Menu.Item leftSection={<IconUser size={16} />} onClick={() => onProfile(row.userId)}>
              {k('action.openProfile')}
            </Menu.Item>
          ) : null}
          {items.map((action) => {
            const Icon = icons[action];
            // Katılmamış kişide Rank change kapalı; ipucu Amend'i gösterir (sahip 2026-10-31)
            const hint = movementActionHint(action, row, row.today);
            return (
              <Menu.Item
                key={action}
                {...(action === 'cancel' || action === 'revoke' ? { color: 'red' } : {})}
                leftSection={<Icon size={16} />}
                disabled={hint !== null}
                onClick={() => onAction(action, row)}
              >
                {k(`action.${action}`)}
                {hint ? (
                  <Text component="span" size="xs" c="dimmed" display="block">
                    {k(`action.${hint}`)}
                  </Text>
                ) : null}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    );
  };
  const status = (row: CrewMovementRow) => (
    <StatusBadge tone={row.status === 'pending' ? 'warning' : 'success'} withIcon>
      {k(`status.${row.status}`)}
    </StatusBadge>
  );
  const port = (row: CrewMovementRow) =>
    row.port || row.portCode ? (
      <span className={classes.next}>
        <span>{row.port ?? row.portCode}</span>
        {row.port && row.portCode ? <span className={classes.date}>{row.portCode}</span> : null}
      </span>
    ) : (
      '—'
    );
  const signOff = tab === 'sign_off';
  const cells = 5 + (signOff ? 1 : 0) + (web ? 1 : 0);
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th scope="col">{k('columns.name')}</th>
          <th scope="col">{k('columns.rank')}</th>
          {web ? <th scope="col">{k('columns.vessel')}</th> : null}
          <th scope="col">{k(signOff ? 'columns.signOffDate' : 'columns.joiningDate')}</th>
          <th scope="col">{k('columns.port')}</th>
          {signOff ? <th scope="col">{k('columns.reason')}</th> : null}
          <th scope="col">{k('columns.status')}</th>
          <th scope="col" className={classes.actionsHead}>
            {k('columns.actions')}
          </th>
        </tr>
      </thead>
      <tbody>
        {loading
          ? Array.from({ length: SKELETON_ROWS }, (_, n) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet satırları
              <tr key={n} aria-hidden>
                {Array.from({ length: cells }, (_, m) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet hücreleri
                  <td key={m}>
                    <Skeleton height={10} width={m === 0 ? '70%' : '55%'} />
                  </td>
                ))}
                <td />
              </tr>
            ))
          : rows.map((row) => (
              <tr key={`${row.assignmentId}-${tab}`}>
                <td className={classes.name}>
                  {row.fullName}
                  {row.crewCode ? <span className={classes.date}> · {row.crewCode}</span> : null}
                </td>
                <td>
                  <span className={classes.contract}>
                    <span>{row.rank ?? '—'}</span>
                    {row.rankChange ? (
                      <StatusBadge tone="info" outlined>
                        {k('rankChangeBadge')}
                      </StatusBadge>
                    ) : null}
                  </span>
                </td>
                {web ? <td>{row.vesselName}</td> : null}
                <td className={classes.date}>{formatDay(row.date)}</td>
                <td>{port(row)}</td>
                {signOff ? (
                  <td>{row.signOffReason ? k(`reason.${row.signOffReason}`) : '—'}</td>
                ) : null}
                <td>{status(row)}</td>
                <td className={classes.actions}>{actions(row)}</td>
              </tr>
            ))}
      </tbody>
    </table>
  );
}
